import type { ReactNode } from 'react';
import type { ClinicInvoiceDTO, ClinicInvoiceLine } from './ClinicInvoiceModal';

export function formatMoney(value: number, locale: string): string {
  return `$ ${value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatPeriodLabel(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  const raw = new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
}

export function periodBounds(period: string, locale: string): { from: string; to: string } {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return { from: period, to: period };
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return {
    from: from.toLocaleDateString(locale),
    to: to.toLocaleDateString(locale),
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function staffingOf(invoice: ClinicInvoiceDTO) {
  if (invoice.staffing) return invoice.staffing;
  const offices = invoice.offices ?? [];
  return {
    consultorios: offices.reduce((s, o) => s + (o.consultorios ?? 0), 0),
    extraConsultorios: offices.reduce((s, o) => s + (o.extraConsultorios ?? 0), 0),
    profesionales: offices.reduce((s, o) => s + (o.profesionales ?? 0), 0),
    extraProfesionales: offices.reduce((s, o) => s + (o.extraProfesionales ?? 0), 0),
  };
}

const FIELD_CLASS =
  'w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-teal-600';

export function InvoiceDocument({
  invoice,
  editing,
  onChangeLine,
  onRemoveLine,
  extraForm,
  t,
  locale,
}: {
  invoice: ClinicInvoiceDTO;
  editing: boolean;
  onChangeLine: (index: number, patch: Partial<ClinicInvoiceLine>) => void;
  onRemoveLine: (index: number) => void;
  extraForm?: ReactNode;
  t: (key: string, opts?: Record<string, unknown>) => string;
  locale: string;
}) {
  const money = (n: number) => formatMoney(n, locale);
  const periodLabel = formatPeriodLabel(invoice.period, locale);
  const bounds = periodBounds(invoice.period, locale);
  const due = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(locale) : '—';
  const generatedAt = new Date(invoice.generatedAt).toLocaleString(locale);
  const staffing = staffingOf(invoice);
  const offices = invoice.offices ?? [];
  const overageBilled = invoice.lines.some((line) => line.kind === 'whatsapp_overage');
  const overQuota = invoice.whatsapp.overageMessages > 0;
  const quota = invoice.whatsapp.quota;
  const usedPct = quota > 0 ? Math.min(100, (invoice.whatsapp.used / quota) * 100) : invoice.whatsapp.used > 0 ? 100 : 0;

  return (
    <article className="invoice-sheet mx-auto w-full max-w-[190mm] bg-white px-5 py-6 text-slate-900 sm:px-8 sm:py-8">
      <header className="flex items-start justify-between gap-6 border-b border-slate-200 pb-5">
        <div className="flex min-w-0 items-start gap-3">
          <img
            src="/assets/coofit-logo.png"
            alt="Coofit"
            className="h-9 w-auto shrink-0 rounded bg-black object-contain px-1.5 py-1"
          />
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              {t('pages.clinicInvoices.docTitle')}
            </p>
            <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">{periodLabel}</p>
            <p className="mt-3 text-sm font-semibold text-slate-900">{invoice.nombre}</p>
            {invoice.email ? <p className="text-xs text-slate-500">{invoice.email}</p> : null}
          </div>
        </div>
        <div className="shrink-0 rounded-md bg-teal-700 px-4 py-3 text-right text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-100">
            {t('pages.clinicInvoices.amountDue')}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{money(invoice.amountDue)}</p>
          {invoice.previousDebt > 0 ? (
            <p className="mt-1 text-[11px] text-teal-100">
              {t('pages.clinicInvoices.previousDebtIncluded', { amount: money(invoice.previousDebt) })}
            </p>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 border-b border-slate-200 py-3 text-xs sm:grid-cols-3">
        <MetaItem
          label={t('pages.clinicInvoices.period')}
          value={`${bounds.from} ${t('pages.clinicInvoices.periodTo')} ${bounds.to}`}
        />
        <MetaItem label={t('pages.clinicInvoices.dueDate')} value={due} />
        <MetaItem label={t('pages.clinicInvoices.generatedAt')} value={generatedAt} />
      </div>

      {editing ? (
        <p className="mt-4 text-xs text-amber-800">{t('pages.clinicInvoices.editHint')}</p>
      ) : null}

      <section className="invoice-section mt-7">
        <h2 className="invoice-h2">{t('pages.clinicInvoices.sectionConcepts')}</h2>
        <div className="overflow-x-auto">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>{t('pages.clinicInvoices.concept')}</th>
                <th className="num">{t('pages.clinicInvoices.qty')}</th>
                <th className="num">{t('pages.clinicInvoices.amount')}</th>
                {editing ? <th className="num">{t('common.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line, i) => (
                <tr key={`${line.kind}-${i}`}>
                  <td>
                    {editing ? (
                      <div className="space-y-1">
                        <input
                          className={FIELD_CLASS}
                          value={line.name}
                          onChange={(e) => onChangeLine(i, { name: e.target.value })}
                        />
                        <input
                          className={`${FIELD_CLASS} text-xs`}
                          value={line.description ?? ''}
                          onChange={(e) => onChangeLine(i, { description: e.target.value || null })}
                          placeholder={t('pages.clinicInvoices.descriptionPlaceholder')}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="font-medium">{line.name}</div>
                        {line.description ? <div className="mt-0.5 text-[11px] text-slate-500">{line.description}</div> : null}
                      </>
                    )}
                  </td>
                  <td className="num">
                    {editing ? (
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className={`${FIELD_CLASS} ml-auto w-16 text-right`}
                        value={line.quantity}
                        onChange={(e) => onChangeLine(i, { quantity: Number(e.target.value) || 0 })}
                      />
                    ) : (
                      line.quantity
                    )}
                  </td>
                  <td className="num font-medium">
                    {editing ? (
                      <input
                        type="number"
                        step="0.01"
                        className={`${FIELD_CLASS} ml-auto w-28 text-right`}
                        value={line.subtotal}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 0;
                          const qty = line.quantity || 1;
                          onChangeLine(i, { unitPrice: next / qty });
                        }}
                      />
                    ) : (
                      money(line.subtotal)
                    )}
                  </td>
                  {editing ? (
                    <td className="num">
                      <button
                        type="button"
                        className="text-xs text-rose-600 hover:underline disabled:text-slate-300"
                        onClick={() => onRemoveLine(i)}
                        disabled={invoice.lines.length <= 1}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>{t('pages.clinicInvoices.invoiceTotal')}</td>
                <td className="num">{money(invoice.total)}</td>
                {editing ? <td /> : null}
              </tr>
            </tfoot>
          </table>
        </div>
        {extraForm}
      </section>

      <section className="invoice-section mt-8">
        <h2 className="invoice-h2">{t('pages.clinicInvoices.sectionOffices')}</h2>
        {offices.length === 0 ? (
          <p className="text-sm text-slate-500">{t('pages.clinicInvoices.noOfficeUsage')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>{t('pages.clinicInvoices.office')}</th>
                  <th className="num">{t('pages.clinicInvoices.colConsultorios')}</th>
                  <th className="num">{t('pages.clinicInvoices.colProfesionales')}</th>
                  <th className="num">{t('pages.clinicInvoices.colWaUsed')}</th>
                  <th className="num">{t('pages.clinicInvoices.share')}</th>
                </tr>
              </thead>
              <tbody>
                {offices.map((row, i) => (
                  <tr key={`${row.officeId ?? 'none'}-${i}`}>
                    <td className="font-medium">{row.officeName}</td>
                    <td className="num">
                      <ResourceCount value={row.consultorios ?? 0} extra={row.extraConsultorios ?? 0} />
                    </td>
                    <td className="num">
                      <ResourceCount value={row.profesionales ?? 0} extra={row.extraProfesionales ?? 0} />
                    </td>
                    <td className="num">{row.messages.toLocaleString(locale)}</td>
                    <td className="num">
                      <UsageBar pct={row.usagePct} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('pages.clinicInvoices.totalRow')}</td>
                  <td className="num">
                    <ResourceCount value={staffing.consultorios} extra={staffing.extraConsultorios} />
                  </td>
                  <td className="num">
                    <ResourceCount value={staffing.profesionales} extra={staffing.extraProfesionales} />
                  </td>
                  <td className="num">{invoice.whatsapp.used.toLocaleString(locale)}</td>
                  <td className="num">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <section className="invoice-section mt-8">
        <h2 className="invoice-h2">{t('pages.clinicInvoices.sectionConexion')}</h2>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
              {t('pages.clinicInvoices.messagesUsed')}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {invoice.whatsapp.used.toLocaleString(locale)}
              <span className="ml-2 text-sm font-normal text-slate-500">
                {t('pages.clinicInvoices.ofQuota', { quota: quota.toLocaleString(locale) })}
              </span>
            </p>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-teal-700" style={{ width: `${Math.max(2, usedPct)}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {t('pages.clinicInvoices.planPct', { pct: usedPct.toFixed(1) })}
              {invoice.whatsapp.bonus > 0
                ? ` · ${t('pages.clinicInvoices.bonusIncluded', { count: invoice.whatsapp.bonus.toLocaleString(locale) })}`
                : ''}
            </p>
          </div>
          <div className={`shrink-0 text-right ${overQuota ? 'text-teal-800' : 'text-slate-400'}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em]">
              {t('pages.clinicInvoices.waOverage')}
            </p>
            <p className={`mt-1 tabular-nums ${overQuota ? 'text-lg font-semibold' : 'text-sm'}`}>
              {invoice.whatsapp.overageMessages.toLocaleString(locale)}
            </p>
            {overQuota && !overageBilled ? (
              <p className="text-sm font-medium tabular-nums">{money(invoice.whatsapp.overageAmount)}</p>
            ) : (
              <p className="text-xs">{overQuota ? t('pages.clinicInvoices.overageInConcepts') : money(0)}</p>
            )}
          </div>
        </div>
      </section>

      <footer className="invoice-footer mt-8 flex flex-col gap-2 border-t border-slate-200 pt-3 text-[10px] leading-relaxed text-slate-400 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-sm">{t('pages.clinicInvoices.disclaimer')}</p>
        <p>
          {invoice.nombre} · {t('pages.clinicInvoices.docTitle')}
        </p>
        <p>{t('pages.clinicInvoices.pageOf')}</p>
      </footer>

      <style>{`
        .invoice-h2 { font-size: 11px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #0f766e; margin: 0 0 10px; }
        .invoice-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .invoice-table th { text-align: left; font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; padding: 0 8px 8px 0; }
        .invoice-table td { padding: 9px 8px 9px 0; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .invoice-table .num { text-align: right; font-variant-numeric: tabular-nums; }
        .invoice-table tfoot td { border-bottom: 0; border-top: 1px solid #cbd5e1; font-weight: 600; padding-top: 10px; }
      `}</style>
    </article>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-700">{value}</p>
    </div>
  );
}

function ResourceCount({ value, extra }: { value: number; extra: number }) {
  return (
    <span className="tabular-nums">
      {value}
      {extra > 0 ? <span className="ml-1 text-[11px] font-medium text-teal-700">(+{extra})</span> : null}
    </span>
  );
}

function UsageBar({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="hidden h-1 w-16 overflow-hidden rounded-full bg-slate-100 sm:inline-block">
        <span className="block h-full rounded-full bg-teal-600/70" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
      </span>
      <span>{pct.toFixed(1)}%</span>
    </span>
  );
}

export function buildPrintHtml(
  invoice: ClinicInvoiceDTO,
  locale: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const money = (n: number) => formatMoney(n, locale);
  const periodLabel = formatPeriodLabel(invoice.period, locale);
  const bounds = periodBounds(invoice.period, locale);
  const due = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(locale) : '—';
  const generatedAt = new Date(invoice.generatedAt).toLocaleString(locale);
  const logoSrc = `${window.location.origin}/assets/coofit-logo.png`;
  const staffing = staffingOf(invoice);
  const offices = invoice.offices ?? [];
  const overageBilled = invoice.lines.some((line) => line.kind === 'whatsapp_overage');
  const overQuota = invoice.whatsapp.overageMessages > 0;
  const quota = invoice.whatsapp.quota;
  const usedPct = quota > 0 ? Math.min(100, (invoice.whatsapp.used / quota) * 100) : invoice.whatsapp.used > 0 ? 100 : 0;
  const previousDebt =
    invoice.previousDebt > 0
      ? `<div class="debt">${escapeHtml(t('pages.clinicInvoices.previousDebtIncluded', { amount: money(invoice.previousDebt) }))}</div>`
      : '';

  const conceptRows = invoice.lines
    .map(
      (line) => `<tr>
        <td>${escapeHtml(line.name)}${line.description ? `<div class="desc">${escapeHtml(line.description)}</div>` : ''}</td>
        <td class="num">${line.quantity}</td>
        <td class="num">${money(line.subtotal)}</td>
      </tr>`
    )
    .join('');

  const officeRows = offices
    .map((row) => {
      const bar = Math.max(2, Math.min(100, row.usagePct));
      return `<tr>
        <td>${escapeHtml(row.officeName)}</td>
        <td class="num">${resourceHtml(row.consultorios ?? 0, row.extraConsultorios ?? 0)}</td>
        <td class="num">${resourceHtml(row.profesionales ?? 0, row.extraProfesionales ?? 0)}</td>
        <td class="num">${row.messages.toLocaleString(locale)}</td>
        <td class="num"><span class="bar"><span style="width:${bar}%"></span></span>${row.usagePct.toFixed(1)}%</td>
      </tr>`;
    })
    .join('');

  const overageHtml = overQuota
    ? `<div class="overage hot">
        <span>${escapeHtml(t('pages.clinicInvoices.waOverage'))}</span>
        <strong>${invoice.whatsapp.overageMessages.toLocaleString(locale)}</strong>
        ${overageBilled ? `<em>${escapeHtml(t('pages.clinicInvoices.overageInConcepts'))}</em>` : `<em>${money(invoice.whatsapp.overageAmount)}</em>`}
      </div>`
    : `<div class="overage">
        <span>${escapeHtml(t('pages.clinicInvoices.waOverage'))}</span>
        <strong>0</strong>
        <em>${money(0)}</em>
      </div>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(t('pages.clinicInvoices.docTitle'))} — ${escapeHtml(invoice.nombre)}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 12mm 12mm 12mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #0f172a; font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; }
    body { font-size: 12.5px; line-height: 1.4; }
    header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; border-bottom: 1px solid #e2e8f0; padding-bottom: 16px; }
    .brand { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
    .brand img { height: 36px; width: auto; background: #000; padding: 4px 8px; border-radius: 4px; }
    .kicker { font-size: 10px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: #64748b; margin: 0; }
    .period { font-size: 18px; font-weight: 650; margin: 2px 0 10px; }
    .clinic { font-size: 13px; font-weight: 650; margin: 0; }
    .email { font-size: 11px; color: #64748b; margin: 2px 0 0; }
    .total { background: #0f766e; color: #fff; padding: 12px 16px; border-radius: 6px; text-align: right; min-width: 180px; }
    .total span { display: block; font-size: 9px; letter-spacing: .14em; text-transform: uppercase; color: #ccfbf1; }
    .total strong { display: block; margin-top: 4px; font-size: 24px; letter-spacing: -.03em; font-variant-numeric: tabular-nums; }
    .debt { margin-top: 4px; font-size: 10px; color: #ccfbf1; }
    .meta { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; border-bottom: 1px solid #e2e8f0; padding: 10px 0 12px; }
    .meta p { margin: 0; }
    .meta .lbl { font-size: 9px; font-weight: 650; letter-spacing: .1em; text-transform: uppercase; color: #94a3b8; }
    .meta .val { margin-top: 2px; font-size: 12px; color: #334155; }
    h2 { font-size: 10px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; color: #0f766e; margin: 22px 0 8px; page-break-after: avoid; break-after: avoid; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 9px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; padding: 0 6px 6px 0; }
    td { padding: 7px 6px 7px 0; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .desc { margin-top: 2px; font-size: 10px; color: #64748b; }
    tfoot td { border-bottom: 0; border-top: 1px solid #cbd5e1; font-weight: 650; padding-top: 8px; }
    .extra { margin-left: 4px; font-size: 10px; font-weight: 650; color: #0f766e; }
    .bar { display: inline-block; width: 52px; height: 4px; background: #f1f5f9; border-radius: 99px; margin-right: 6px; vertical-align: middle; overflow: hidden; }
    .bar span { display: block; height: 100%; background: rgba(13,148,136,.7); }
    .conexion { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
    .used { font-size: 22px; font-weight: 650; margin: 4px 0 0; }
    .used small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 6px; }
    .track { height: 5px; background: #f1f5f9; border-radius: 99px; margin-top: 10px; overflow: hidden; }
    .track span { display: block; height: 100%; background: #0f766e; }
    .hint { margin: 6px 0 0; font-size: 11px; color: #64748b; }
    .overage { text-align: right; color: #94a3b8; }
    .overage span { display: block; font-size: 9px; letter-spacing: .12em; text-transform: uppercase; }
    .overage strong { display: block; margin-top: 2px; font-size: 13px; }
    .overage em { display: block; font-style: normal; font-size: 11px; }
    .overage.hot { color: #115e59; }
    .overage.hot strong { font-size: 18px; }
    footer { display: flex; justify-content: space-between; gap: 16px; border-top: 1px solid #e2e8f0; margin-top: 22px; padding-top: 10px; color: #94a3b8; font-size: 9px; line-height: 1.45; page-break-inside: avoid; break-inside: avoid; }
    footer p { margin: 0; }
    .section { page-break-inside: auto; }
    tr, h2 { page-break-inside: avoid; break-inside: avoid; }
    thead { display: table-header-group; }
    @media print {
      html, body { background: #fff; }
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  </style>
</head>
<body>
  <header>
    <div class="brand">
      <img src="${escapeHtml(logoSrc)}" alt="Coofit" />
      <div>
        <p class="kicker">${escapeHtml(t('pages.clinicInvoices.docTitle'))}</p>
        <p class="period">${escapeHtml(periodLabel)}</p>
        <p class="clinic">${escapeHtml(invoice.nombre)}</p>
        ${invoice.email ? `<p class="email">${escapeHtml(invoice.email)}</p>` : ''}
      </div>
    </div>
    <div class="total">
      <span>${escapeHtml(t('pages.clinicInvoices.amountDue'))}</span>
      <strong>${money(invoice.amountDue)}</strong>
      ${previousDebt}
    </div>
  </header>
  <div class="meta">
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.period'))}</p><p class="val">${escapeHtml(bounds.from)} ${escapeHtml(t('pages.clinicInvoices.periodTo'))} ${escapeHtml(bounds.to)}</p></div>
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.dueDate'))}</p><p class="val">${escapeHtml(due)}</p></div>
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.generatedAt'))}</p><p class="val">${escapeHtml(generatedAt)}</p></div>
  </div>
  <section class="section">
    <h2>${escapeHtml(t('pages.clinicInvoices.sectionConcepts'))}</h2>
    <table>
      <thead><tr>
        <th>${escapeHtml(t('pages.clinicInvoices.concept'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.qty'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.amount'))}</th>
      </tr></thead>
      <tbody>${conceptRows}</tbody>
      <tfoot><tr>
        <td colspan="2">${escapeHtml(t('pages.clinicInvoices.invoiceTotal'))}</td>
        <td class="num">${money(invoice.total)}</td>
      </tr></tfoot>
    </table>
  </section>
  ${
    offices.length > 0
      ? `<section class="section">
    <h2>${escapeHtml(t('pages.clinicInvoices.sectionOffices'))}</h2>
    <table>
      <thead><tr>
        <th>${escapeHtml(t('pages.clinicInvoices.office'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.colConsultorios'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.colProfesionales'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.colWaUsed'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.share'))}</th>
      </tr></thead>
      <tbody>${officeRows}</tbody>
      <tfoot><tr>
        <td>${escapeHtml(t('pages.clinicInvoices.totalRow'))}</td>
        <td class="num">${resourceHtml(staffing.consultorios, staffing.extraConsultorios)}</td>
        <td class="num">${resourceHtml(staffing.profesionales, staffing.extraProfesionales)}</td>
        <td class="num">${invoice.whatsapp.used.toLocaleString(locale)}</td>
        <td class="num">100%</td>
      </tr></tfoot>
    </table>
  </section>`
      : ''
  }
  <section class="section">
    <h2>${escapeHtml(t('pages.clinicInvoices.sectionConexion'))}</h2>
    <div class="conexion">
      <div>
        <p class="kicker">${escapeHtml(t('pages.clinicInvoices.messagesUsed'))}</p>
        <p class="used">${invoice.whatsapp.used.toLocaleString(locale)}<small>${escapeHtml(t('pages.clinicInvoices.ofQuota', { quota: quota.toLocaleString(locale) }))}</small></p>
        <div class="track"><span style="width:${Math.max(2, usedPct)}%"></span></div>
        <p class="hint">${escapeHtml(t('pages.clinicInvoices.planPct', { pct: usedPct.toFixed(1) }))}${
          invoice.whatsapp.bonus > 0
            ? ` · ${escapeHtml(t('pages.clinicInvoices.bonusIncluded', { count: invoice.whatsapp.bonus.toLocaleString(locale) }))}`
            : ''
        }</p>
      </div>
      ${overageHtml}
    </div>
  </section>
  <footer>
    <p>${escapeHtml(t('pages.clinicInvoices.disclaimer'))}</p>
    <p>${escapeHtml(invoice.nombre)} · ${escapeHtml(t('pages.clinicInvoices.docTitle'))}</p>
    <p>${escapeHtml(t('pages.clinicInvoices.pageOf'))}</p>
  </footer>
</body>
</html>`;
}

function resourceHtml(value: number, extra: number): string {
  return `${value}${extra > 0 ? `<span class="extra">(+${extra})</span>` : ''}`;
}
